import { useState, type FC } from "react";
import type {
  PermissionSets,
  SkynetUserGroupPermissions,
} from "../../../models/RolesAndPermissionsModels";
import styled from "styled-components";
import { Col, Row } from "react-grid-system";
import { theme } from "../../../theme";
import Body from "../../atoms/typography/Body";
import Checkbox from "../../atoms/inputs/Checkbox";
import ChevronButton from "../../atoms/buttons/ChevronButtons";
import TableRow from "../../atoms/TableRow";
import BodySmall from "../../atoms/typography/BodySmall";
import { useTranslation } from "react-i18next";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import TableHeaderText, {
  TableHeaderTextSorting,
} from "../../atoms/typography/TableHeaderText";
import { InfoIconSmall } from "../../atoms/icons/InfoIcons";
import Tooltip from "../Tooltip";

interface Props {
  userGroupPermissions: SkynetUserGroupPermissions;
  editable: boolean;
  onPermissionsChange?: (perms: SkynetUserGroupPermissions) => void;
  showMaxPermissions: boolean;
}

const GroupSectionRow = styled(Row)<{ $isLast?: boolean }>`
  background-color: ${theme.colors.grey02};
  padding: 8px;
  border-top: 2px solid ${theme.colors.grey05};
  ${({ $isLast }) =>
    $isLast && `border-bottom: 2px solid ${theme.colors.grey05};`}
`;

const ALL_ADMIN_PERMS = [
  "CONFIGURATION",
  "LEGAL",
  "RULE",
  "USER",
  "USER_GROUP",
] as const;
const ALL_AML_PERMS = [
  "AML_EXPORT",
  "AML_LOG",
  "AML_MONITORING",
  "AML_RISK_SCORE",
  "BLACKLIST",
  "DEBT_COLLECTION",
] as const;
const ALL_CLIENT_PERMS = [
  "ACCOUNT_PLAN",
  "EMAIL_ADDRESS_CONNECTION",
  "LIMITS",
  "PAYMENT",
  "SETTINGS",
  "SETTLEMENT",
  "USER",
] as const;
const ALL_FILE_PERMS = ["INVOICE_DOCUMENT_INGRESS", "SETTLEMENT"] as const;
const ALL_INVOICE_PERMS = [
  "FORCE_ASSESSMENT_APPROVAL",
  "INVOICE",
  "INVOICE_INTERPRETATION",
  "INVOICE_UPLOAD_UNIT",
  "EXEMPTION_VERIFIERA",
  "EXEMPTION_RECIPIENT_APPROVAL_RULES",
  "EXEMPTION_OTHER_RULES",
  "EXEMPTION_LIMITS_RULES",
  "EXEMPTION_INTERNAL_INVOICING",
  "EXEMPTION_BLACKLIST_RULES",
] as const;
const ALL_ONBOARDING_PERMS = [
  "AGREEMENT",
  "BANK",
  "INTERNAL_KYC",
  "INTERNAL_SALES",
  "KYC",
  "SALES",
  "CREDIT_APPROVAL",
  "FINALIZE",
  "PROSPECT",
] as const;

const SECTION_PERMS: Record<
  keyof SkynetUserGroupPermissions,
  readonly string[]
> = {
  admin: ALL_ADMIN_PERMS,
  aml: ALL_AML_PERMS,
  client: ALL_CLIENT_PERMS,
  file: ALL_FILE_PERMS,
  invoice: ALL_INVOICE_PERMS,
  onboarding: ALL_ONBOARDING_PERMS,
};

const GroupsPermissionsList: FC<Props> = ({
  userGroupPermissions,
  editable,
  onPermissionsChange,
  showMaxPermissions,
}) => {
  const { t } = useTranslation();
  const [openPermissions, setOpenPermissions] = useState<Array<string>>([]);

  const [prevShowMaxPermissions, setPrevShowMaxPermissions] =
    useState(showMaxPermissions);
  if (showMaxPermissions !== prevShowMaxPermissions) {
    setPrevShowMaxPermissions(showMaxPermissions);
    if (showMaxPermissions) {
      setOpenPermissions(Object.keys(SECTION_PERMS));
    }
  }

  const togglePermission = (
    module: keyof SkynetUserGroupPermissions,
    perm: string,
    field: "defaultRead" | "defaultWrite",
  ) => {
    if (!onPermissionsChange) return;
    const current = userGroupPermissions[module] ?? {
      defaultRead: [],
      defaultWrite: [],
      maxRead: [],
      maxWrite: [],
    };
    const arr = current[field] ?? [];
    const isRemoving = arr.includes(perm);
    const next: PermissionSets = {
      ...current,
      [field]: isRemoving ? arr.filter((p) => p !== perm) : [...arr, perm],
    };
    if (field === "defaultWrite" && !isRemoving) {
      const readArr = current.defaultRead ?? [];
      if ((current.maxRead ?? []).includes(perm) && !readArr.includes(perm)) {
        next.defaultRead = [...readArr, perm];
      }
    }
    onPermissionsChange({
      ...userGroupPermissions,
      [module]: next,
    });
  };

  const toggleMaxPermission = (
    module: keyof SkynetUserGroupPermissions,
    perm: string,
    field: "maxRead" | "maxWrite",
  ) => {
    if (!onPermissionsChange) return;
    const current = userGroupPermissions[module] ?? {
      defaultRead: [],
      defaultWrite: [],
      maxRead: [],
      maxWrite: [],
    };
    const arr = current[field] ?? [];
    const isRemoving = arr.includes(perm);
    const correspondingDefault =
      field === "maxRead" ? "defaultRead" : "defaultWrite";
    const defaultArr = current[correspondingDefault] ?? [];
    const next: PermissionSets = {
      ...current,
      [field]: isRemoving ? arr.filter((p) => p !== perm) : [...arr, perm],
      // If unchecking a max permission, also remove it from defaults
      [correspondingDefault]: isRemoving
        ? defaultArr.filter((p) => p !== perm)
        : defaultArr,
    };
    if (field === "maxWrite" && !isRemoving) {
      const maxReadArr = current.maxRead ?? [];
      if (!maxReadArr.includes(perm)) {
        next.maxRead = [...maxReadArr, perm];
      }
    }
    onPermissionsChange({
      ...userGroupPermissions,
      [module]: next,
    });
  };

  const hasAssignablePerms = (module: keyof SkynetUserGroupPermissions) => {
    const mod = userGroupPermissions[module];
    return SECTION_PERMS[module].some(
      (p) => mod?.maxRead.includes(p) || mod?.maxWrite.includes(p),
    );
  };

  //only display sections with available permissions
  const visibleSections = (
    Object.keys(SECTION_PERMS) as Array<keyof SkynetUserGroupPermissions>
  ).filter((module) => showMaxPermissions || hasAssignablePerms(module));
  const lastVisibleSection = visibleSections[visibleSections.length - 1];

  const toggleGroupSectionOpen = (
    permissionTitle:
      | "admin"
      | "aml"
      | "client"
      | "file"
      | "invoice"
      | "onboarding",
  ) => {
    if (openPermissions.includes(permissionTitle)) {
      setOpenPermissions(
        openPermissions.filter((title) => title !== permissionTitle),
      );
    } else {
      setOpenPermissions([...openPermissions, permissionTitle]);
    }
  };

  const renderPermRow = (
    label: string,
    module: keyof SkynetUserGroupPermissions,
    perm: string,
  ) => {
    const mod = userGroupPermissions[module];
    const defaultRead = mod?.defaultRead ?? [];
    const defaultWrite = mod?.defaultWrite ?? [];
    const maxRead = mod?.maxRead ?? [];
    const maxWrite = mod?.maxWrite ?? [];

    return (
      <TableRow key={`${module}-${perm}`} clickable={false}>
        <Col lg={4}>
          <BodySmall>{label}</BodySmall>
        </Col>
        <Col lg={2} style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <Checkbox
            title={t("random.read")}
            checked={defaultRead.includes(perm)}
            onChange={() => togglePermission(module, perm, "defaultRead")}
            disabled={!editable || !maxRead.includes(perm)}
          />
        </Col>
        <Col lg={2} style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <Checkbox
            title={t("random.write")}
            checked={defaultWrite.includes(perm)}
            onChange={() => togglePermission(module, perm, "defaultWrite")}
            disabled={!editable || !maxWrite.includes(perm)}
          />
        </Col>
        {showMaxPermissions && (
          <>
            <Col
              lg={2}
              style={{ display: "flex", gap: 4, alignItems: "center" }}
            >
              <Checkbox
                title={t("random.read")}
                checked={maxRead.includes(perm)}
                onChange={() => toggleMaxPermission(module, perm, "maxRead")}
                disabled={!editable}
              />
            </Col>
            <Col
              lg={2}
              style={{ display: "flex", gap: 4, alignItems: "center" }}
            >
              <Checkbox
                title={t("random.write")}
                checked={maxWrite.includes(perm)}
                onChange={() => toggleMaxPermission(module, perm, "maxWrite")}
                disabled={!editable}
              />
            </Col>
          </>
        )}
      </TableRow>
    );
  };

  const columnHeader = (
    <TableRow clickable={false}>
      <Col lg={4}>
        <TableHeaderText>Beskrivning</TableHeaderText>
      </Col>
      <Col lg={4}>
        <TableHeaderTextSorting
          otherIcon={
            <Tooltip text="Alla som ingår i gruppen kommer automatisk tilldelas dessa rättigheter">
              <InfoIconSmall />
            </Tooltip>
          }
        >
          Inkluderade
        </TableHeaderTextSorting>
      </Col>
      {showMaxPermissions && (
        <Col lg={4}>
          <TableHeaderTextSorting
            otherIcon={
              <Tooltip text="Gruppadmin kan styra vilka användare som får dessa rättigheter">
                <InfoIconSmall />
              </Tooltip>
            }
          >
            Möjliga
          </TableHeaderTextSorting>
        </Col>
      )}
    </TableRow>
  );

  return (
    <div>
      {visibleSections.includes("admin") && (
        <div>
          <GroupSectionRow
            justify="between"
            align="center"
            nogutter
            onClick={() => toggleGroupSectionOpen("admin")}
            $isLast={lastVisibleSection === "admin"}
          >
            <Body fontweight="500">Admin</Body>
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              <ChevronButton
                type={openPermissions.includes("admin") ? "up" : "down"}
                onClick={() => toggleGroupSectionOpen("admin")}
              />
            </div>
          </GroupSectionRow>
          <SlideOutHeight isVisible={openPermissions.includes("admin")}>
            {columnHeader}
            {renderPermRow("Användare", "admin", "USER")}
            {renderPermRow("Regler", "admin", "RULE")}
            {renderPermRow("Legal", "admin", "LEGAL")}
            {renderPermRow("Konfigurering", "admin", "CONFIGURATION")}
            {renderPermRow("Användargrupper", "admin", "USER_GROUP")}
          </SlideOutHeight>
        </div>
      )}

      {visibleSections.includes("aml") && (
        <div>
          <GroupSectionRow
            justify="between"
            align="center"
            nogutter
            onClick={() => toggleGroupSectionOpen("aml")}
            $isLast={lastVisibleSection === "aml"}
          >
            <Body fontweight="500">AML</Body>
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              <ChevronButton
                type={openPermissions.includes("aml") ? "up" : "down"}
                onClick={() => toggleGroupSectionOpen("aml")}
              />
            </div>
          </GroupSectionRow>
          <SlideOutHeight isVisible={openPermissions.includes("aml")}>
            {columnHeader}

            {renderPermRow("Riskbetyg", "aml", "AML_RISK_SCORE")}
            {renderPermRow("Monitoreringar", "aml", "AML_MONITORING")}
            {renderPermRow("Loggar", "aml", "AML_LOG")}
            {renderPermRow("Export", "aml", "AML_EXPORT")}
            {renderPermRow("Blacklist", "aml", "BLACKLIST")}
            {renderPermRow("Debt collection?", "aml", "DEBT_COLLECTION")}
          </SlideOutHeight>
        </div>
      )}

      {visibleSections.includes("client") && (
        <div>
          <GroupSectionRow
            justify="between"
            align="center"
            nogutter
            onClick={() => toggleGroupSectionOpen("client")}
            $isLast={lastVisibleSection === "client"}
          >
            <Body fontweight="500">Klienter</Body>
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              <ChevronButton
                type={openPermissions.includes("client") ? "up" : "down"}
                onClick={() => toggleGroupSectionOpen("client")}
              />
            </div>
          </GroupSectionRow>
          <SlideOutHeight isVisible={openPermissions.includes("client")}>
            {columnHeader}

            {renderPermRow(
              "Epostkopplingar",
              "client",
              "EMAIL_ADDRESS_CONNECTION",
            )}
            {renderPermRow("Kontoplan", "client", "ACCOUNT_PLAN")}
            {renderPermRow("Användare", "client", "USER")}
            {renderPermRow("Inställningar", "client", "SETTINGS")}
            {renderPermRow("Avräkningar", "client", "SETTLEMENT")}
            {renderPermRow("Inbetalningar", "client", "PAYMENT")}
            {renderPermRow("Limiter", "client", "LIMITS")}
          </SlideOutHeight>
        </div>
      )}

      {visibleSections.includes("file") && (
        <div>
          <GroupSectionRow
            justify="between"
            align="center"
            nogutter
            onClick={() => toggleGroupSectionOpen("file")}
            $isLast={lastVisibleSection === "file"}
          >
            <Body fontweight="500">Filer</Body>
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              <ChevronButton
                type={openPermissions.includes("file") ? "up" : "down"}
                onClick={() => toggleGroupSectionOpen("file")}
              />
            </div>
          </GroupSectionRow>
          <SlideOutHeight isVisible={openPermissions.includes("file")}>
            {columnHeader}

            {renderPermRow("Avräkningar", "file", "SETTLEMENT")}
            {renderPermRow(
              "Dokumenthantering",
              "file",
              "INVOICE_DOCUMENT_INGRESS",
            )}
          </SlideOutHeight>
        </div>
      )}

      {visibleSections.includes("invoice") && (
        <div>
          <GroupSectionRow
            justify="between"
            align="center"
            nogutter
            onClick={() => toggleGroupSectionOpen("invoice")}
            $isLast={lastVisibleSection === "invoice"}
          >
            <Body fontweight="500">Fakturor</Body>
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              <ChevronButton
                type={openPermissions.includes("invoice") ? "up" : "down"}
                onClick={() => toggleGroupSectionOpen("invoice")}
              />
            </div>
          </GroupSectionRow>
          <SlideOutHeight isVisible={openPermissions.includes("invoice")}>
            {columnHeader}

            {renderPermRow(
              "Dokumenthantering",
              "invoice",
              "INVOICE_UPLOAD_UNIT",
            )}
            {renderPermRow(
              "Fakturatolkning",
              "invoice",
              "INVOICE_INTERPRETATION",
            )}
            {renderPermRow("Fakturor", "invoice", "INVOICE")}
            {renderPermRow(
              "Forcera fakturabeslut",
              "invoice",
              "FORCE_ASSESSMENT_APPROVAL",
            )}
            {renderPermRow(
              "Undantag - Verifiera",
              "invoice",
              "EXEMPTION_VERIFIERA",
            )}
            {renderPermRow(
              "Undantag kring mottagare",
              "invoice",
              "EXEMPTION_RECIPIENT_APPROVAL_RULES",
            )}
            {renderPermRow(
              "Undantag övriga",
              "invoice",
              "EXEMPTION_OTHER_RULES",
            )}
            {renderPermRow(
              "Undantag limiter",
              "invoice",
              "EXEMPTION_LIMITS_RULES",
            )}
            {renderPermRow(
              "Undantag internfakturering",
              "invoice",
              "EXEMPTION_INTERNAL_INVOICING",
            )}
            {renderPermRow(
              "Undantag blacklist",
              "invoice",
              "EXEMPTION_BLACKLIST_RULES",
            )}
          </SlideOutHeight>
        </div>
      )}

      {visibleSections.includes("onboarding") && (
        <div>
          <GroupSectionRow
            justify="between"
            align="center"
            nogutter
            onClick={() => toggleGroupSectionOpen("onboarding")}
            $isLast={lastVisibleSection === "onboarding"}
          >
            <Body fontweight="500">Onboarding</Body>
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              <ChevronButton
                type={openPermissions.includes("onboarding") ? "up" : "down"}
                onClick={() => toggleGroupSectionOpen("onboarding")}
              />
            </div>
          </GroupSectionRow>
          <SlideOutHeight isVisible={openPermissions.includes("onboarding")}>
            {columnHeader}

            {renderPermRow("Bank", "onboarding", "BANK")}
            {renderPermRow("Avtal", "onboarding", "AGREEMENT")}
            {renderPermRow("KYC", "onboarding", "KYC")}
            {renderPermRow("Intern KYC", "onboarding", "INTERNAL_KYC")}
            {renderPermRow("Sälj", "onboarding", "SALES")}
            {renderPermRow("Sälj internt", "onboarding", "INTERNAL_SALES")}
            {renderPermRow(
              "Kreditgodkännande",
              "onboarding",
              "CREDIT_APPROVAL",
            )}
            {renderPermRow("Prospekt", "onboarding", "PROSPECT")}
            {renderPermRow("Slutför", "onboarding", "FINALIZE")}
          </SlideOutHeight>
        </div>
      )}
    </div>
  );
};

export default GroupsPermissionsList;
